import { useLocation } from 'react-router-dom';

import { BottomNavigation } from '@mui/material';
import NavSection from './NavSection';
import navConfig from './navConfig';

import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import { useHasPermission } from '../../../store/useAdminStore';
import { getRouteObjects } from '../../../routes';
import { isBottomMenuHiddenForPath } from '../../../routes/routeAccess';

/**
 * Renders a bottom navigation bar based on the user's authentication status and environment configurations.
 * This component uses the `useAuth` hook to fetch authentication status and feature configurations,
 * and conditionally renders navigation elements based on these values.
 * If the user is not authenticated or essential data is missing, no navigation is rendered.
 * Otherwise, it dynamically generates a bottom navigation bar based on the navigation configuration
 * determined by the user's environment, role, and enabled features.
 *
 * @returns {JSX.Element|null} A styled bottom navigation bar if conditions are met, otherwise null.
 */
const BottomNavigationBar = () => {
  const { isAuthenticated, env, user, features } = useAuth();
  const { translate } = useLocales();
  const { pathname } = useLocation();
  const { isIos } = window.env;
  // CW-21903 : `useHasPermission` souscrit aux permissions/rôle dans Zustand
  // pour que ce composant re-render dès qu'elles arrivent. Voir
  // useAdminStore.ts pour le détail du mécanisme.
  const hasPermission = useHasPermission();

  if (!isAuthenticated || !env || !user || !features) {
    return null;
  }

  if (isBottomMenuHiddenForPath(getRouteObjects(), pathname)) {
    return null;
  }

  const config = navConfig.get(env, user, features, translate, hasPermission);
  const columns = config[0].items.length || 5;

  return (
    <BottomNavigation
      id="bottom-bar"
      className="bottom-navigation"
      showLabels
      sx={styleBottomNavigation(isIos, columns)}
    >
      <NavSection isShow isMobile />
    </BottomNavigation>
  );
};

const styleBottomNavigation = (isIos, columns) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  paddingBottom: isIos ? 0 : 'env(safe-area-inset-bottom)',

  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  gridTemplateRows: 'auto',

  height: 'auto',
  overflow: 'visible',
  zIndex: 1200
});

export default BottomNavigationBar;
